/**
 * Shared boot menu prefetch gate — one claim per customer across `menu` and `foodMenu` domains.
 */
let boot_claimed = false;
let boot_complete = false;
let boot_customer_id: number | null = null;
let last_fingerprint = '';
let fingerprint_stable_timer: ReturnType<typeof setTimeout> | null = null;
const complete_listeners = new Set<() => void>();

export const BOOT_MENU_PREFETCH_STABLE_MS = 400;
export const BOOT_MENU_FIRST_BATCH_SIZE = 3;

export function resetBootMenuPrefetchGate(): void {
  boot_claimed = false;
  boot_complete = false;
  boot_customer_id = null;
  last_fingerprint = '';
  if (fingerprint_stable_timer) {
    clearTimeout(fingerprint_stable_timer);
    fingerprint_stable_timer = null;
  }
}

/** Returns true only for the first successful claim for this customer. */
export function tryClaimBootMenuPrefetch(customer_id: number): boolean {
  if (boot_customer_id !== customer_id) {
    boot_claimed = false;
    boot_complete = false;
    boot_customer_id = customer_id;
  }
  if (boot_claimed) {
    return false;
  }
  boot_claimed = true;
  boot_complete = false;
  return true;
}

export function markBootMenuPrefetchComplete(): void {
  boot_complete = true;
  complete_listeners.forEach(listener => listener());
}

export function isBootMenuPrefetchComplete(): boolean {
  return boot_complete;
}

/** Subscribe to boot-prefetch completion (for neighbor walks that wait on the batch). */
export function subscribeBootMenuPrefetchComplete(listener: () => void): () => void {
  if (boot_complete) {
    listener();
  }
  complete_listeners.add(listener);
  return () => {
    complete_listeners.delete(listener);
  };
}

/**
 * Runs `on_stable` once fingerprint stays unchanged for {@link BOOT_MENU_PREFETCH_STABLE_MS}.
 * Cancels prior pending callbacks when fingerprint changes.
 */
export function runWhenSubscriptionFingerprintStable(
  fingerprint: string,
  on_stable: () => void,
  delay_ms: number = BOOT_MENU_PREFETCH_STABLE_MS,
): () => void {
  if (fingerprint_stable_timer) {
    clearTimeout(fingerprint_stable_timer);
    fingerprint_stable_timer = null;
  }
  if (!fingerprint || fingerprint.length < 2) {
    return () => {};
  }
  if (fingerprint !== last_fingerprint) {
    last_fingerprint = fingerprint;
  }
  fingerprint_stable_timer = setTimeout(() => {
    fingerprint_stable_timer = null;
    if (last_fingerprint === fingerprint) {
      on_stable();
    }
  }, delay_ms);
  return () => {
    if (fingerprint_stable_timer) {
      clearTimeout(fingerprint_stable_timer);
      fingerprint_stable_timer = null;
    }
  };
}
